/**
 * Code-based TanStack Router tree.
 *
 * File-based routing is the more idiomatic TanStack approach, but it
 * requires a codegen step (`@tanstack/router-vite-plugin` emits
 * `routeTree.gen.ts`). At this scale code-based is still simpler; if
 * the route table grows past ~10 entries we'll switch.
 */

import { Outlet, createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { Suspense, lazy, type ReactNode } from 'react';

import { RoleGuard } from './auth/RoleGuard';
import { CustomerPortalPreviewPage } from './routes/customers.$slug.preview';
import { CustomerDetailPage } from './routes/customers.$slug';
import { CustomersPage } from './routes/customers';
import { RootLayout } from './routes/__root';
import { EngagementDetailPage } from './routes/engagements.$id';
import { NewRunPage } from './routes/engagements.$id.runs.new';
import { AskKbPage } from './routes/ask-kb';
import { IndexPage } from './routes/index';
import { LabelingPage } from './routes/labeling';
import { PortalPage } from './routes/portal';
import { RuleSetsPage } from './routes/rule_sets';
import { SignInDonePage } from './routes/sign_in.done';
import { SignInPage } from './routes/sign_in';
import { RulesPage } from './routes/rules';
import { FindingDetailPage } from './routes/runs.$id.findings.$findingId';
import { RunDetailPage } from './routes/runs.$id';
import {
  SupplierViewByIdPage,
  SupplierViewPage,
} from './routes/runs.$id.suppliers.$supplierName';
import { SettingsPage } from './routes/settings';

/** Wrap a route component with the internal-only role guard. Customers
 *  hitting any of these get bounced to /portal; practitioners pass. */
const internal = (Page: () => ReactNode) => () => (
  <RoleGuard audience="practitioner">{Page()}</RoleGuard>
);

// The supplier-network page pulls in react-force-graph (~300 kB), so it's
// code-split into its own chunk rather than loaded on every practitioner
// session — only fetched when the route is opened.
const SupplierNetworkPage = lazy(() =>
  import('./routes/runs.$id.network').then((m) => ({ default: m.SupplierNetworkPage })),
);

/** Customer-only wrapper for /portal. Practitioners hitting it get
 *  bounced to /accounts (no admin-impersonation view yet). */
const portal = (Page: () => ReactNode) => () => (
  <RoleGuard audience="customer">{Page()}</RoleGuard>
);

const rootRoute = createRootRoute({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: internal(IndexPage),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: internal(SettingsPage),
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: internal(CustomersPage),
});

const customerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$slug',
  component: internal(CustomerDetailPage),
});

const customerPortalPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$slug/preview',
  component: internal(CustomerPortalPreviewPage),
});

const engagementDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/engagements/$id',
  component: internal(EngagementDetailPage),
});

// Sits at /engagements/$id/runs/new — registered as its own top-level
// route rather than a nested child because TanStack code-based routing
// resolves siblings by path, not parent-child nesting.
const newRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/engagements/$id/runs/new',
  component: internal(NewRunPage),
});

const runDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$id',
  component: internal(RunDetailPage),
});

const supplierNetworkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$id/network',
  component: () => (
    <RoleGuard audience="practitioner">
      <Suspense
        fallback={
          <div
            role="status"
            style={{
              position: 'fixed',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              background: '#0b1220',
              color: '#93a6c6',
              fontSize: 14,
            }}
          >
            Loading supplier network…
          </div>
        }
      >
        <SupplierNetworkPage />
      </Suspense>
    </RoleGuard>
  ),
});

const supplierViewByIdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$id/suppliers/by-id/$supplierId',
  component: internal(SupplierViewByIdPage),
});

const supplierViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$id/suppliers/$supplierName',
  component: internal(SupplierViewPage),
});

const findingDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$id/findings/$findingId',
  component: internal(FindingDetailPage),
});

const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rules',
  component: internal(RulesPage),
});

const ruleSetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rule-sets',
  component: internal(RuleSetsPage),
});

const askKbRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ask-kb',
  component: internal(AskKbPage),
});

const labelingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/labeling',
  component: internal(LabelingPage),
});

// Deep link to label one specific invoice by UUID — same page, the card is
// claimed from the param. Lets a browse row open in a new tab / be bookmarked.
const labelingDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/labeling/$documentId',
  component: internal(LabelingPage),
});

// Legacy /accounts* paths — the tenant entity renamed to "customers"
// (INP-419); bookmarks and old Slack links keep working.
const legacyAccountsRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts',
  beforeLoad: ({ location }) => {
    throw redirect({ to: '/customers', search: location.search, replace: true });
  },
});

const legacyAccountDetailRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$slug',
  beforeLoad: ({ params, location }) => {
    throw redirect({ to: '/customers/$slug', params, search: location.search, replace: true });
  },
});

const legacyAccountPreviewRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$slug/preview',
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: '/customers/$slug/preview',
      params,
      search: location.search,
      replace: true,
    });
  },
});

const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  component: portal(PortalPage),
});

// Both sign-in routes carry an optional `redirect` search param — the
// deep link a signed-out user was trying to reach. RoleGuard writes it,
// /sign-in threads it through Clerk's redirect URL, and /sign-in/done
// honors it (sanitized) over the role-home dispatch.
const signInSearch = (search: Record<string, unknown>): { redirect?: string } => ({
  redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: signInSearch,
  component: SignInPage,
});

const signInDoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/done',
  validateSearch: signInSearch,
  component: SignInDonePage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  settingsRoute,
  customersRoute,
  customerDetailRoute,
  customerPortalPreviewRoute,
  legacyAccountsRedirect,
  legacyAccountDetailRedirect,
  legacyAccountPreviewRedirect,
  engagementDetailRoute,
  newRunRoute,
  runDetailRoute,
  supplierNetworkRoute,
  supplierViewByIdRoute,
  supplierViewRoute,
  findingDetailRoute,
  rulesRoute,
  ruleSetsRoute,
  askKbRoute,
  labelingRoute,
  labelingDocumentRoute,
  portalRoute,
  signInRoute,
  signInDoneRoute,
]);

// Re-export for tests / dev tools.
export const createAppRouter = () => createRouter({ routeTree });
